import React, { useState } from "react";
import { useGetProjectBasedLearning } from "@packages/api/madlenAPI";
import { Expand, X, Target } from "lucide-react";
import { Button, Text } from "@core/index";
import Markdown from "react-markdown";
import { ProjectBasedLearningResponseDto } from "@packages/api/madlenAPI.schemas";
import { useTranslation } from "react-i18next";
import ProjectBasedLearningCard from "@features/tools/components/project-based-learning/ProjectBasedLearningCard";
import SpinnerLoader from "../components/SpinnerLoader";

interface ProjectBasedLearningProps {
  id: number | null;
}

const ProjectBasedLearningDetailComponent = ({
  project,
  closeModal,
}: {
  project: ProjectBasedLearningResponseDto;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-auto bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>
            {project.title || t("projectBasedLearning.title")}
          </Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          <ProjectBasedLearningCard project={project} />
        </div>
      </div>
    </div>
  );
};

const ProjectBasedLearning: React.FC<ProjectBasedLearningProps> = ({ id }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useGetProjectBasedLearning(id, {
    query: {
      refetchInterval: (data) => {
        if (
          data.state.data?.realLifeConnection &&
          data.state.data.realLifeConnection?.length > 0
        ) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  const isContentLoading =
    isLoading || data.projectProblem?.questions?.length === 0;

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          {t("projectBasedLearning.error.unableToLoad")}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("projectBasedLearning.error.tryRegenerate")}
        </p>
      </div>
    );
  }

  return (
    <>
      {expanded && data && (
        <ProjectBasedLearningDetailComponent
          project={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex w-full justify-between items-center">
          <h3 className="text-xl font-medium mb-4 flex items-center justify-between text-gray-900 dark:text-gray-100">
            <div>{t("projectBasedLearning.title")}</div>
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("projectBasedLearning.subtitle")}
        </p>

        <div className="grid grid-cols-1 gap-4">
          {data.projectProblem?.questions?.length &&
            data.projectProblem.questions.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-xl bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400">
                    <Target size={20} />
                  </div>
                  <h4 className="font-semibold text-red-600 dark:text-red-400">
                    {t("projectBasedLearning.projectProblem")}
                  </h4>
                </div>
                <div className="leading-relaxed text-gray-800 dark:text-gray-200">
                  {data.projectProblem.questions
                    .slice(0, 1)
                    .map((question, idx) => (
                      <Markdown key={idx} className="prose dark:prose-invert">
                        {question}
                      </Markdown>
                    ))}
                </div>
              </div>
            )}

          {/* Real Life Connection Section */}
          {data.realLifeConnection && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
                  <Target size={20} />
                </div>
                <h4 className="font-semibold text-green-600 dark:text-green-400">
                  {t("projectBasedLearning.realLifeConnection")}
                </h4>
              </div>
              <div className="leading-relaxed text-gray-800 dark:text-gray-200">
                <Markdown className="prose dark:prose-invert">
                  {data.realLifeConnection}
                </Markdown>
              </div>
            </div>
          )}

          {/* Process Steps Section */}
          {data.processSteps && data.processSteps.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
                  <Target size={20} />
                </div>
                <h4 className="font-semibold text-purple-600 dark:text-purple-400">
                  {t("projectBasedLearning.processSteps")}
                </h4>
              </div>
              <div className="space-y-2">
                {data.processSteps.slice(0, 1).map((step, idx) => (
                  <Text
                    key={idx}
                    variant="p"
                    className="leading-relaxed text-gray-800 dark:text-gray-200"
                  >
                    <Markdown className="prose dark:prose-invert">
                      {step}
                    </Markdown>
                  </Text>
                ))}
              </div>
            </div>
          )}

          {/* Feedback Points Section */}
          {data.feedbackPoints && data.feedbackPoints.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400">
                  <Target size={20} />
                </div>
                <h4 className="font-semibold text-amber-600 dark:text-amber-400">
                  {t("projectBasedLearning.feedbackPoints")}
                </h4>
              </div>
              <div className="space-y-2">
                {data.feedbackPoints.slice(0, 1).map((point, idx) => (
                  <Text
                    key={idx}
                    variant="p"
                    className="leading-relaxed text-gray-800 dark:text-gray-200"
                  >
                    <Markdown className="prose dark:prose-invert">
                      {point}
                    </Markdown>
                  </Text>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {data.assessment?.criteria &&
              data.assessment.criteria.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2 text-gray-900 dark:text-gray-100">
                    {t("projectBasedLearning.assessmentCriteria")}:
                  </h5>
                  <ul className="list-disc pl-5 space-y-1">
                    {data.assessment.criteria
                      .slice(0, 1)
                      .map((criterion: string, idx: number) => (
                        <li
                          key={idx}
                          className="leading-relaxed text-gray-800 dark:text-gray-200"
                        >
                          <div>{criterion}</div>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            {data.assessment?.rubric?.some(
              (item) => item?.criterion && item?.levels,
            ) && (
              <div>
                <h5 className="font-medium mb-2 text-gray-900 dark:text-gray-100">
                  {t("projectBasedLearning.assessmentRubric")}:
                </h5>
                {data.assessment.rubric
                  .filter((item) => item?.criterion && item?.levels)
                  .slice(0, 1)
                  .map((item: any, idx: number) => (
                    <div key={idx} className="mb-4">
                      <h6 className="font-medium mb-1 text-gray-800 dark:text-gray-200">
                        <div>{item.criterion}</div>
                      </h6>
                      <ul className="list-disc pl-5 space-y-1">
                        {item.levels
                          ?.slice(0, 1)
                          .map((level: string, levelIdx: number) => (
                            <li
                              key={levelIdx}
                              className="leading-relaxed text-gray-700 dark:text-gray-300"
                            >
                              <div>{level}</div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            {data.peerSelfAssessment?.peer_review_questions?.length &&
              data.peerSelfAssessment.peer_review_questions.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2 text-gray-900 dark:text-gray-100">
                    {t("projectBasedLearning.peerReviewQuestions")}:
                  </h5>
                  <ul className="list-disc pl-5 space-y-1">
                    {data.peerSelfAssessment.peer_review_questions
                      .slice(0, 1)
                      .map((question: string, idx: number) => (
                        <li
                          key={idx}
                          className="leading-relaxed text-gray-700 dark:text-gray-300"
                        >
                          <div>{question}</div>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            {data.peerSelfAssessment?.self_reflection_questions?.length &&
              data.peerSelfAssessment.self_reflection_questions.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2 text-gray-900 dark:text-gray-100">
                    {t("projectBasedLearning.selfReflectionQuestions")}:
                  </h5>
                  <ul className="list-disc pl-5 space-y-1">
                    {data.peerSelfAssessment.self_reflection_questions
                      .slice(0, 1)
                      .map((question: string, idx: number) => (
                        <li
                          key={idx}
                          className="leading-relaxed text-gray-700 dark:text-gray-300"
                        >
                          <div>{question}</div>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectBasedLearning;
